/**
 * HeatmapStrip — Horizontal consistency heatmap
 * Each race = colored cell (green = high, yellow = avg, red = DNF/low)
 */

import { lazy, Suspense, useMemo } from "react";
import { getChartTheme, useThemeVersion } from "./chart-theme";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface HeatmapStripProps {
  labels: string[];
  values: number[];
  height?: number;
  maxValue?: number;
  /**
   * Formats the tooltip value. Defaults to "{value} pts" — pass a custom
   * formatter when the cell encodes something other than points (e.g. an
   * inverted finishing position or a DNF count in race context).
   */
  valueFormatter?: (value: number, label: string) => string;
}

export function HeatmapStrip({
  labels,
  values,
  height = 80,
  maxValue,
  valueFormatter,
}: HeatmapStripProps) {
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const theme = getChartTheme();
    const max = maxValue ?? Math.max(...values, 1);

    const data = values.map((v, i) => [i, 0, v]);

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
        formatter: (params: { data: number[] }) => {
          const idx = params.data[0];
          const value = params.data[2];
          const formatted = valueFormatter
            ? valueFormatter(value, labels[idx])
            : `${value} pts`;
          return `${labels[idx]}: ${formatted}`;
        },
      },
      grid: {
        left: 8,
        right: 8,
        top: 4,
        bottom: 24,
      },
      xAxis: {
        type: "category" as const,
        data: labels,
        axisLabel: {
          color: theme.mutedForeground,
          fontSize: 9,
          interval: Math.max(0, Math.floor(labels.length / 10) - 1),
          rotate: labels.length > 15 ? 45 : 0,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category" as const,
        data: [""],
        show: false,
      },
      visualMap: {
        min: -20,
        max,
        calculable: false,
        orient: "horizontal" as const,
        show: false,
        inRange: {
          color: ["#ef4444", "#eab308", "#22c55e"],
        },
      },
      series: [
        {
          type: "heatmap" as const,
          data,
          itemStyle: {
            borderRadius: 3,
            borderWidth: 2,
            borderColor: "transparent",
          },
          emphasis: {
            itemStyle: { borderColor: theme.foreground },
          },
          animationDuration: 500,
          animationDelay: (idx: number) => idx * 30,
        },
      ],
    };
  }, [labels, values, height, maxValue, valueFormatter, themeVersion]);

  return (
    <Suspense fallback={<div style={{ height: `${height}px` }} />}>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Suspense>
  );
}
